import type { ActorClipName } from './actor-motion.ts';

interface Body { id: string; x: number; y: number; appearance: string; dead: boolean }
interface Contact { age: number; x: number; y: number; force: number; approach: number; receiving: boolean }
export interface ContactPose { clip: ActorClipName; x: number; y: number; facing: string }

/** Physical punctuation of an already-resolved close blow. These offsets never
 * reach navigation, range checks or damage. Both bodies share one contact axis. */
export class MeleeContact {
  #bodies = new Map<string, Contact>();

  clear(): void { this.#bodies.clear(); }

  meet(receiver: Body, source: Body, scale: number, strength: number): void {
    const dx = receiver.x - source.x, dy = receiver.y - source.y, gap = Math.hypot(dx, dy);
    // A distant spear tip or arrow is a wound, not a clinch. A dead body cannot push.
    if (receiver.dead || source.dead || source.appearance.includes('scout') || gap > 72 * scale || gap < 1) return;
    const force = Number.isFinite(strength) ? Math.max(.3, Math.min(1, strength / 9)) : .5;
    const approach = Math.max(0, gap / scale - 26);
    this.#bodies.set(source.id, { age: 0, x: dx / gap, y: dy / gap, force, approach: approach * .82, receiving: false });
    this.#bodies.set(receiver.id, { age: 0, x: -dx / gap, y: -dy / gap, force, approach: approach * .18, receiving: true });
  }

  tick(ms: number): void {
    if (ms <= 0) return;
    for (const [id, body] of this.#bodies) {
      body.age += ms;
      if (body.age >= 620) this.#bodies.delete(id);
    }
  }

  pose(id: string): ContactPose | undefined {
    const body = this.#bodies.get(id);
    if (!body) return;
    const { age, receiving, force, approach } = body;
    // Compressed arms meet, hold against pressure, then the rear leg takes the
    // weight as the bodies part. The final planted recovery reaches zero once.
    const release = Math.max(0, Math.min(1, (age - 190) / 300));
    const bind = 1 - release * release * (3 - 2 * release);
    const breakStep = age < 250 ? 0 : Math.sin(Math.PI * Math.min(1, (age - 250) / 370));
    const forward = approach * bind - (receiving ? 5 : 3) * breakStep * force;
    // Each lead foot steps outside the opposing stance. The paired poses lean
    // inward; this shoulder offset keeps both heads visible during the bind.
    const shoulder = 8 * bind;
    return { clip: age < 80 && receiving ? 'recoil' : age < 240 ? 'bind' : age < 410 && !receiving ? 'shove' : 'receive',
      x: body.x * forward + body.y * shoulder, y: body.y * forward - body.x * shoulder,
      facing: ['e', 'se', 's', 'sw', 'w', 'nw', 'n', 'ne'][(Math.round(Math.atan2(body.y, body.x) / (Math.PI / 4)) + 8) % 8]! };
  }
}
